import { useEffect, useRef, useMemo } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'
import { WaypointResponse } from '@/shared/api/routes'

export interface UseItineraryWaypointMarkersProps {
  waypoints?: WaypointResponse[]
  selectedRouteId?: string | null
  selectedTripId?: string | null // Only show markers when viewing an itinerary
  disabled?: boolean // Disable this hook (e.g., when using itinerary builder markers)
}

/**
 * Hook for displaying numbered markers for itinerary waypoints
 * Shows numbered badges (based on order_index) on the map for waypoints associated with a trip
 * After the route refactor, waypoints are trip-centric and may not have route_id initially
 * (routes are auto-generated from trip waypoints)
 * Only displays markers when selectedTripId is set (viewing an itinerary)
 */
export function useItineraryWaypointMarkers({ waypoints = [], selectedRouteId, selectedTripId, disabled = false }: UseItineraryWaypointMarkersProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const markersRef = useRef<mapboxgl.Marker[]>([])

  // Filter to only itinerary waypoints (have trip_id, route_id is optional)
  // If selectedRouteId is provided, only show waypoints for that route
  // Otherwise show all waypoints for the trip (including those without routes)
  // IMPORTANT: Only process waypoints when selectedTripId is set (viewing an itinerary)
  const itineraryWaypoints = useMemo(() => {
    // If not viewing an itinerary, return empty array immediately
    if (!selectedTripId || !waypoints.length) {
      return []
    }

    return waypoints
      .filter(wp => {
        const wpRouteId = wp.route_id || (wp as any).routeId
        const hasTripId = wp.trip_id != null
        const hasCoords = wp.latitude != null && wp.longitude != null
        
        // Must have trip_id and coordinates
        // route_id is optional - waypoints can be added to trips before routes are generated
        // Also ensure trip_id matches selectedTripId to avoid showing waypoints from other trips
        if (!(hasTripId && hasCoords)) {
          return false
        }

        // Only show waypoints that belong to the currently selected trip
        if (wp.trip_id !== selectedTripId) {
          return false
        }

        // If selectedRouteId is provided, filter by it (only show waypoints for that route)
        if (selectedRouteId) {
          return wpRouteId === selectedRouteId
        }

        // Otherwise show all trip waypoints (including those without route_id)
        return true
      })
      .sort((a, b) => {
        // Sort by route_id first (if present), then by order_index
        // Waypoints without route_id will be sorted together by order_index
        const routeIdA = a.route_id || (a as any).routeId || ''
        const routeIdB = b.route_id || (b as any).routeId || ''
        
        // If both have route_id, sort by route_id first
        if (routeIdA && routeIdB && routeIdA !== routeIdB) {
          return routeIdA.localeCompare(routeIdB)
        }

        // Otherwise sort by order_index (waypoints without routes will be sorted together)
        const orderA = a.order_index ?? 999999
        const orderB = b.order_index ?? 999999
        return orderA - orderB
      })
  }, [waypoints, selectedRouteId, selectedTripId])

  useEffect(() => {
    // Skip if disabled (e.g., when using itinerary builder markers)
    if (disabled) {
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
      return
    }

    // Only show markers when viewing an itinerary (selectedTripId is set)
    if (!selectedTripId) {
      // Clear all markers when not viewing itinerary
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
      return
    }

    if (!map.current || !isLoaded || !styleLoaded) {
      // Clear markers if map not ready
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
      return
    }

    // If no itinerary waypoints to show, clear existing markers
    if (itineraryWaypoints.length === 0) {
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
      return
    }

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove())
    markersRef.current = []

    // Create numbered markers for each itinerary waypoint
    itineraryWaypoints.forEach((waypoint) => {
      const lat = waypoint.latitude
      const lng = waypoint.longitude
      const orderIndex = waypoint.order_index ?? 0

      if (lat == null || lng == null || isNaN(Number(lat)) || isNaN(Number(lng))) {
        return
      }

      // Create numbered badge marker
      const markerEl = document.createElement('div')
      markerEl.className = 'itinerary-waypoint-marker'
      
      // Determine badge color: first waypoint (0) gets adventure blue, others get earth brown
      const isFirst = orderIndex === 0
      const bgColor = isFirst ? 'bg-adventure-600' : 'bg-earth-600'
      const number = orderIndex + 1 // Display 1-based index

      markerEl.innerHTML = `
        <div class="flex items-center justify-center w-8 h-8 rounded-full ${bgColor} text-white text-xs font-bold shadow-lg border-2 border-white">
          ${number}
        </div>
      `
      markerEl.style.cursor = 'pointer'

      const marker = new mapboxgl.Marker({
        element: markerEl,
        anchor: 'center'
      })
        .setLngLat([lng, lat])
        .addTo(map.current!)

      markersRef.current.push(marker)
    })

    return () => {
      markersRef.current.forEach(marker => marker.remove())
      markersRef.current = []
    }
  }, [map, isLoaded, styleLoaded, itineraryWaypoints, selectedTripId])

  return { itineraryWaypoints }
}

